import React from 'react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface StatCardProps {
  title: string
  value: string | number
  unit?: string
  description?: string
  icon?: React.ReactNode
  /** Percentage change vs prior window. Positive = up. */
  delta?: number
  /** Tiny inline trend chart series. */
  spark?: number[]
  /** Adds a live-pulse dot in the corner. */
  live?: boolean
  /** Highlight the card with the brand gradient ring. */
  accent?: boolean
}

export function StatCard({
  title,
  value,
  unit,
  description,
  icon,
  delta,
  spark,
  live,
  accent,
}: StatCardProps) {
  const formattedValue =
    typeof value === 'number' ? value.toLocaleString() : value

  return (
    <div
      className={cn(
        'surface-card relative overflow-hidden p-5 sm:p-6',
        accent && 'signal-border',
      )}
    >
      {accent && (
        <div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full"
          style={{
            background:
              'radial-gradient(circle, hsl(var(--brand-2) / 0.18) 0%, transparent 70%)',
          }}
        />
      )}

      <div className="relative flex items-start justify-between gap-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {title}
        </div>
        <div className="flex items-center gap-2">
          {live && <span className="live-dot" aria-label="live" />}
          {icon && <span className="text-muted-foreground/80">{icon}</span>}
        </div>
      </div>

      <div className="relative mt-3 flex items-baseline gap-1.5">
        <span className="font-heading text-5xl leading-none tracking-tight tabular-nums">
          {formattedValue}
        </span>
        {unit && (
          <span className="font-mono text-xs text-muted-foreground">{unit}</span>
        )}
      </div>

      <div className="relative mt-3 flex items-center justify-between gap-3">
        {typeof delta === 'number' ? (
          <DeltaPill delta={delta} />
        ) : description ? (
          <span className="text-xs text-muted-foreground">{description}</span>
        ) : (
          <span />
        )}
        {spark && spark.length > 1 && (
          <Sparkline data={spark} positive={(delta ?? 0) >= 0} />
        )}
      </div>
    </div>
  )
}

function DeltaPill({ delta }: { delta: number }) {
  const positive = delta >= 0
  const Icon = positive ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em]',
        positive
          ? 'bg-[hsl(var(--signal-live)/0.15)] text-[hsl(var(--signal-live))]'
          : 'bg-destructive/15 text-destructive',
      )}
    >
      <Icon className="h-3 w-3" />
      {Math.abs(delta).toFixed(1)}% · 24h
    </span>
  )
}

function Sparkline({ data, positive }: { data: number[]; positive: boolean }) {
  const w = 84
  const h = 28
  const max = Math.max(1, ...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const step = w / Math.max(1, data.length - 1)

  const points = data.map((v, i) => {
    const x = i * step
    const y = h - ((v - min) / range) * h
    return [x, y] as const
  })

  const path =
    points.reduce(
      (acc, [x, y], i) => acc + (i === 0 ? `M${x.toFixed(1)} ${y.toFixed(1)}` : ` L${x.toFixed(1)} ${y.toFixed(1)}`),
      '',
    )
  const fillPath = `${path} L${w.toFixed(1)} ${h.toFixed(1)} L0 ${h.toFixed(1)} Z`
  const stroke = positive ? 'hsl(var(--brand-1))' : 'hsl(var(--destructive))'
  const fill = positive
    ? 'hsl(var(--brand-1) / 0.18)'
    : 'hsl(var(--destructive) / 0.18)'

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <path d={fillPath} fill={fill} />
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="2"
          fill={stroke}
        />
      )}
    </svg>
  )
}
